import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  PresentationChartLineIcon,
  CurrencyDollarIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const SocialMediaPage: React.FC = () => {
  const navigate = useNavigate();

  // SEO setup
  useSEO(pageConfigs.socialMedia);

  const handleGetAudit = () => {
    navigate('/consultation');
  };

  const handleViewCaseStudies = () => {
    navigate('/case-studies');
  };

  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };

  const handleGetStarted = () => {
    navigate('/quote');
  };

  const handleLearnMore = () => {
    navigate('/about');
  };

  const features = [
    {
      title: "Content Strategy & Creation",
      description: "Develop a comprehensive content strategy and create engaging content that resonates with your audience.",
      icon: <ChatBubbleLeftRightIcon className="w-8 h-8" />,
      benefits: ["Content calendar", "Brand voice development", "Visual content creation", "Copywriting"]
    },
    {
      title: "Community Management",
      description: "Build and nurture an engaged community around your brand with professional community management.",
      icon: <UserGroupIcon className="w-8 h-8" />,
      benefits: ["Engagement monitoring", "Comment management", "Customer service", "Crisis management"]
    },
    {
      title: "Paid Social Advertising",
      description: "Maximize your reach and ROI with strategic paid social media advertising campaigns.",
      icon: <CurrencyDollarIcon className="w-8 h-8" />,
      benefits: ["Campaign strategy", "Audience targeting", "Ad creative", "Budget optimization"]
    },
    {
      title: "Analytics & Reporting",
      description: "Track performance and make data-driven decisions with comprehensive social media analytics.",
      icon: <PresentationChartLineIcon className="w-8 h-8" />,
      benefits: ["Performance tracking", "Audience insights", "Competitor analysis", "ROI reporting"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Social Audit & Strategy",
      description: "We analyze your current social presence, competitors, and audience to develop a tailored strategy."
    },
    {
      step: "02",
      title: "Content Planning",
      description: "Create a comprehensive content calendar and develop engaging content that aligns with your brand."
    },
    {
      step: "03",
      title: "Implementation & Management",
      description: "Execute the strategy across all relevant social platforms with consistent posting and engagement."
    },
    {
      step: "04",
      title: "Analysis & Optimization",
      description: "Continuously monitor performance and optimize your social media presence for maximum results."
    }
  ];

  const caseStudy = {
    client: "Lifestyle Brand",
    industry: "Fashion & Apparel",
    challenge: "Low social media engagement and struggling to build a cohesive brand presence",
    solution: "Implemented comprehensive social media strategy with consistent content and community management",
    results: [
      "400% follower growth in 12 months",
      "85% increase in engagement rate",
      "250% increase in website traffic from social",
      "320% increase in social-driven sales"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.socialMedia} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                <UserGroupIcon className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-semibold text-sm">Engagement Experts</span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
                Professional 
                <span className="text-blue-600"> Social Media Management</span>
              </h1>
              
              <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                Build brand authority and drive engagement with professional social media management.
                Our strategies create meaningful connections with your audience.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={handleGetAudit}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Get Social Media Audit</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleViewCaseStudies}
                  className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View Case Studies
                </button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">400%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Follower Growth</div>
                  <div className="text-xs text-slate-500">Average for clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">85%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Engagement Rate</div>
                  <div className="text-xs text-slate-500">Increase</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">250%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Traffic Increase</div>
                  <div className="text-xs text-slate-500">From social</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">320%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Sales Growth</div>
                  <div className="text-xs text-slate-500">Social-driven</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <UserGroupIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Social Media Audit</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get a comprehensive analysis of your current social media performance and 
                  receive a customized strategy to improve your results.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Profile optimization review</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Content strategy assessment</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Custom growth recommendations</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleGetAudit}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Get Your Free Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Comprehensive Social Media Management
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our full-service approach ensures your brand builds authority and drives engagement
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{feature.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Our 4-Step Process
            </h2>
            <p className="text-xl text-slate-600">
              Proven methodology for social media success
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
                  <StarIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                    <p className="text-slate-600">{caseStudy.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution</h4>
                    <p className="text-slate-600">{caseStudy.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Results</h4>
                    <div className="space-y-2">
                      {caseStudy.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckIcon className="w-5 h-5 text-green-600" />
                          <span className="text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">📱</div>
                  <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">Ready to transform your social presence?</h3>
                  <p className="text-slate-600">Schedule a consultation with our social media experts</p>
                </div>
                
                <button 
                  onClick={handleScheduleConsultation}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors mb-4"
                >
                  Schedule Consultation
                </button>
                
                <button 
                  onClick={handleViewCaseStudies}
                  className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View More Case Studies
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Platforms We Manage
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive management across all major social media platforms
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📘</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Facebook</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📸</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Instagram</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🐦</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Twitter</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">💼</div>
              <h3 className="text-xl font-display font-bold text-slate-900">LinkedIn</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📹</div>
              <h3 className="text-xl font-display font-bold text-slate-900">YouTube</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">🎵</div>
              <h3 className="text-xl font-display font-bold text-slate-900">TikTok</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">📌</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Pinterest</h3>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-5xl mb-4">👻</div>
              <h3 className="text-xl font-display font-bold text-slate-900">Snapchat</h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-display font-bold mb-6">
              Ready to Transform Your Social Media Presence?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Get started with a free social media audit and custom strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleGetStarted}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors"
              >
                Get Started
              </button>
              <button 
                onClick={handleLearnMore}
                className="bg-transparent hover:bg-white/10 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/20 transition-colors"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMediaPage;